import classNames from 'classnames'
/* Global imports */
import { FormikContext } from 'formik'
import { map, values } from 'lodash/fp'
import DeleteIcon from 'mdi-react/DeleteIcon'
import React, { useCallback, useContext, useMemo } from 'react'
import { useMuiSelectUtils } from 'scenes/Dashboard/components-next/Inputs/Select/Select.hook'
import EditorField from 'scenes/Dashboard/components/OrganizationDetailsModal/components/OrganizationDetailsEdit/components/Signals/components/EditorField'
import { getIn } from 'utils-next/forms'

import { createStyles, IconButton, MenuItem, Select, SelectChangeEvent, Theme, Typography } from '@mui/material'
import { WithStyles, withStyles } from '@mui/styles'

/* Local imports */

/* Types  */

export enum WhyAFit {
  findFitInWebsite = 'find-fit-in-website',
  findFitInCard = 'find-fit-in-card',
  sampleCompany = 'sample-company',
}

type WhyItIsAFitOptionType = {
  value: string
  text: string
}

type OuterWhyItIsAFitPropsType = {
  className?: string
  fit?: WhyAFit
  name: string
}

type WhyItIsAFitPropsType = OuterWhyItIsAFitPropsType & WithStyles<typeof styles>
/* Local utility functions */

export const WhyAFitTemplate: {
  [key in WhyAFit]: string[]
} = {
  [WhyAFit.findFitInWebsite]: [
    'Fit is based on a solution/technology/patent that is not shown on the company card. For further information on the fit, check this reference',
  ],
  [WhyAFit.findFitInCard]: ['The information in the card below should help you understand why the company is a fit.'],
  [WhyAFit.sampleCompany]: ['You provided this company as a sample fit with the campaign.'],
}

const getTemplate = (reason: string) => {
  let template = ''

  const fitTemplate = WhyAFitTemplate[reason]
  if (fitTemplate && fitTemplate.length) {
    template += fitTemplate.reduce((accumulated: string, templateRow: string) => `${accumulated}<br>${templateRow}`)
  }

  return template
}

/* Component definition */
const WhyItIsAFit = ({ classes, name, className, fit }: WhyItIsAFitPropsType) => {
  const formik = useContext(FormikContext)
  const WhyItIsAFitOptionsArray = useMemo(
    () => map((x) => ({ value: x, text: x }))(values(WhyAFit)),
    []
  ) as WhyItIsAFitOptionType[]

  const { getItem, setSelectedItem, selectedItem } = useMuiSelectUtils(WhyItIsAFitOptionsArray, fit || '')
  const onFitChange = useCallback(
    (event: SelectChangeEvent<{ value: unknown }>) => {
      setSelectedItem(event.target.value as string)
      // as soon as the user chose a value from the selector the resultant template by choice
      // will be saved at form level
      formik.setFieldValue(name, getTemplate(event.target.value as string))
    },
    [name]
  )

  const onRemoveItem = useCallback(() => formik.setFieldValue(name, ''), [formik])
  const onSummaryChange = useCallback((html: string) => formik.setFieldValue(name, html), [formik])

  const error = getIn(formik.errors, name)
  const value = getIn(formik.values, name)

  return (
    <div className={classNames(className, classes.container)}>
      <hr className={classes.hr} />
      <EditorField label="Fit Reason text" onChange={onSummaryChange} value={value} />

      <Typography className={classes.label} variant="caption">
        Select a Fit
      </Typography>

      <Select
        variant="standard"
        value={getItem(selectedItem)}
        onChange={onFitChange}
        disabled={formik.isSubmitting}
        renderValue={() => getItem(selectedItem)?.text}
      >
        {WhyItIsAFitOptionsArray.map((fit) => (
          <MenuItem key={fit.value} value={fit.value}>
            {fit.text}
          </MenuItem>
        ))}
      </Select>

      <footer className={classes.footer}>
        <div className={classes.error}>{error || ''}</div>
        <IconButton aria-label="Delete why it is a fit" onClick={onRemoveItem}>
          <DeleteIcon />
        </IconButton>
      </footer>
    </div>
  )
}
const styles = ({ spacing, palette }: Theme) =>
  createStyles({
    container: {
      padding: `${spacing(2)}px ${spacing(3)}px`,
    },
    label: {
      display: 'block',
      marginBottom: spacing(1),
    },
    error: {
      color: palette.extra.negative,
      fontWeight: 500,
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    hr: {
      border: '2px solid rgba(0,0,0,0.2)',
      borderRadius: '10px',
    },
  })

export default withStyles(styles)(WhyItIsAFit)
